import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@context/LanguageContext'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { useEscapeKey } from '@hooks/useEscapeKey'
import { useAutoFocus } from '@hooks/useAutoFocus'
import { cx } from '@utils/cx'
import { CloseIcon, About, Contact, Privacy, Terms } from '../Icons'
import styles from './MenuModal.module.css'
import baseStyles from '@styles/modalBase.module.css'

export default function MenuModal ({ isOpen, onClose }) {
  const navigate = useNavigate()
  const { t, lang } = useLanguage()
  const modalRef = useRef(null)
  const messageRef = useRef(null)

  useFocusTrap(modalRef)
  useEscapeKey(onClose, isOpen)
  useAutoFocus(messageRef, isOpen)

  const handleNavigation = (page) => {
    navigate(`/${lang}/${page}`)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className={baseStyles.overlayBottom} role='dialog' aria-modal='true' aria-labelledby='menu-title'>
      <div className={styles.menumodal} ref={modalRef}>
        <div className={baseStyles.bottomHandle} />

        <header className={styles.header}>
          <h2
            id='menu-title'
            className={styles.title}
            ref={messageRef}
            tabIndex='-1'
          >
            {t.core.menu.title}
          </h2>
          <button
            onClick={onClose}
            className={styles.close}
            aria-label={t.core.access.menu.close}
            title={t.core.access.menu.close}
          >
            <CloseIcon size={20} stroke={1.5} aria-hidden='true' />
            <span className='sr-only'>{t.core.access.menu.close}</span>
          </button>
        </header>

        <nav className={styles.content} aria-label={t.core.access.menu.navigation}>

          {/* Support */}
          <button
            onClick={() => handleNavigation('support')}
            className={cx(styles.item, styles.itemPrimary)}
            aria-label={t.core.access.navigation.support}
          >
            <span className={styles.itemPrimaryText}>{t.core.menu.support.title}</span>
            <span className={styles.itemDescription}>{t.core.menu.support.description}</span>
          </button>

          {/* Navigation links */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t.core.menu.sections.info}</h3>

            <button
              onClick={() => handleNavigation('about')}
              className={styles.item}
              aria-label={t.core.access.navigation.about}
            >
              <div className={styles.itemHeader}>
                <About size={18} stroke={1.5} aria-hidden='true' />
                <span className={styles.itemText}>{t.core.navigation.about}</span>
              </div>
            </button>

            <button
              onClick={() => handleNavigation('contact')}
              className={styles.item}
              aria-label={t.core.access.navigation.contact}
            >
              <div className={styles.itemHeader}>
                <Contact size={18} stroke={1.5} aria-hidden='true' />
                <span className={styles.itemText}>{t.core.navigation.contact}</span>
              </div>
            </button>
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t.core.menu.sections.legal}</h3>

            <button
              onClick={() => handleNavigation('privacy')}
              className={styles.item}
              aria-label={t.core.access.navigation.privacy}
            >
              <div className={styles.itemHeader}>
                <Privacy size={18} stroke={1.5} aria-hidden='true' />
                <span className={styles.itemText}>{t.core.navigation.privacy}</span>
              </div>
            </button>

            <button
              onClick={() => handleNavigation('terms')}
              className={styles.item}
              aria-label={t.core.access.navigation.terms}
            >
              <div className={styles.itemHeader}>
                <Terms size={18} stroke={1.5} aria-hidden='true' />
                <span className={styles.itemText}>{t.core.navigation.terms}</span>
              </div>
            </button>
          </div>
        </nav>
      </div>
    </div>
  )
}
